<template>
  <section class="page-panel list-page records-list">
    <div class="list-page__header grid grid-gap-16 grid-gap-sm-0 align-items-center">
      <h2 class="col-12 col-sm-6">Zapisnici o primopredaji vozila</h2>

      <ToolbarActionButton
        class="col-12 col-sm-6 justify-self-sm-end"
        text="Novi zapisnik o preuzimanju"
        :disabled="!newPickupRoute"
        :to="newPickupRoute"
      />
    </div>

    <Filters
      v-model="state.filters"
      :filter-fields="filterFields"
      @change="loadRecordsAsync(1)"
    />

    <v-alert
      v-if="state.errorMessage"
      class="mt-5"
      type="error"
      variant="tonal"
    >
      {{ state.errorMessage }}
      <template #append>
        <v-btn
          class="button"
          variant="text"
          @click="loadRecordsAsync(state.currentPage)"
        >
          Ponovi
        </v-btn>
      </template>
    </v-alert>

    <template v-else>
      <v-data-table-server
        class="table list-table records-list__table"
        :headers="recordTableHeaders"
        :items="state.records"
        :items-length="state.totalResults"
        :items-per-page="state.itemsPerPage"
        :loading="state.isLoadingRecords"
        :mobile="false"
        :page="state.currentPage"
        item-value="id"
        loading-text="Učitavanje zapisnika..."
        no-data-text="Nema zapisnika"
        hide-default-footer
      >
        <template #item.vehicleName="{ item }">
          {{ getVehicleDisplayName(item, 'brand-first') }}
        </template>

        <template #item.fullName="{ item }">
          {{ getRecordUserLabel(item) }}
        </template>

        <template #item.usageFrom="{ item }">
          {{ getRecordPeriod(item) }}
        </template>

        <template #item.status="{ item }">
          <span
            class="status-chip"
            :class="recordStatusClasses[item.status]"
          >
            {{ recordStatusLabels[item.status] }}
          </span>
        </template>

        <template #item.pickup="{ item }">
          <div class="records-list__document-actions">
            <v-btn
              class="button records-list__document-button"
              :class="isRecordStageEditable(item, 'pickup') ? 'button--dark' : 'button--secondary'"
              :disabled="!getRecordPageRoute(item, 'pickup')"
              :to="getRecordPageRoute(item, 'pickup')"
              size="small"
              :variant="isRecordStageEditable(item, 'pickup') ? 'flat' : 'outlined'"
            >
              {{ isRecordStageEditable(item, 'pickup') ? 'Otvori zapisnik o preuzimanju' : 'Pregled' }}
            </v-btn>
            <v-btn
              v-if="item.pickupCompletedAt"
              class="button button--secondary records-list__document-button"
              :disabled="Boolean(state.downloadingPdf)"
              :loading="state.downloadingPdf === `${item.id}-pickup`"
              prepend-icon="mdi-download-outline"
              size="small"
              variant="outlined"
              @click="downloadRecordPdfAsync(item, 'pickup')"
            >
              Preuzmi PDF
            </v-btn>
          </div>
        </template>

        <template #item.return="{ item }">
          <div
            v-if="item.status !== 'draft'"
            class="records-list__document-actions"
          >
            <v-btn
              class="button records-list__document-button"
              :class="isRecordStageEditable(item, 'return') ? 'button--dark' : 'button--secondary'"
              :disabled="!getRecordPageRoute(item, 'return')"
              :to="getRecordPageRoute(item, 'return')"
              size="small"
              :variant="isRecordStageEditable(item, 'return') ? 'flat' : 'outlined'"
            >
              {{ isRecordStageEditable(item, 'return') ? 'Otvori zapisnik o vraćanju' : 'Pregled' }}
            </v-btn>
            <v-btn
              v-if="item.returnCompletedAt"
              class="button button--secondary records-list__document-button"
              :disabled="Boolean(state.downloadingPdf)"
              :loading="state.downloadingPdf === `${item.id}-return`"
              prepend-icon="mdi-download-outline"
              size="small"
              variant="outlined"
              @click="downloadRecordPdfAsync(item, 'return')"
            >
              Preuzmi PDF
            </v-btn>
          </div>
        </template>

        <template #item.actions="{ item }">
          <v-menu
            v-if="item.status === 'draft'"
            content-class="table-menu"
            location="bottom end"
          >
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                aria-label="Otvori akcije zapisnika"
                class="button table__actions-button"
                :disabled="state.isDeletingRecord"
                icon="mdi-dots-horizontal"
                size="small"
                variant="text"
              />
            </template>

            <div class="table-menu__content grid-stack grid-gap-16">
              <v-btn
                class="button button--menu button--text-danger table-menu__button--danger"
                variant="text"
                @click="openDeleteDialog(item)"
              >
                <v-icon
                  class="table-menu__icon"
                  icon="mdi-trash-can-outline"
                  size="16"
                />
                Ukloni zapisnik
              </v-btn>
            </div>
          </v-menu>
        </template>
      </v-data-table-server>

      <Pagination
        :page="state.currentPage"
        :per-page="state.itemsPerPage"
        :total-results="state.totalResults"
        @page-change="loadRecordsAsync"
      />
    </template>

    <AppConfirmDialog
      v-model="state.deleteDialogOpen"
      title="Ukloni zapisnik?"
      :subtitle="selectedRecordSubtitle"
      message="Jeste li sigurni da želite ukloniti ovaj zapisnik?"
      primary-text="Ukloni"
      secondary-text="Odustani"
      :loading="state.isDeletingRecord"
      :close-on-confirm="false"
      @close="state.selectedRecord = null"
      @confirm="deleteRecordAsync"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppConfirmDialog from '../../components/AppConfirmDialog.vue'
import Filters from '../../components/Filters.vue'
import Pagination from '../../components/Pagination.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { downloadResponseFileAsync, formatDate, getEventBrandName, getPageCount, getVehicleBrandOptions, getVehicleDisplayName } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
const newPickupRoute = router.hasRoute('fleet-record-pickup-create')
  ? { name: 'fleet-record-pickup-create' }
  : null
let latestRecordsRequestId = 0
const brandOptions = getVehicleBrandOptions()

const state = reactive({
  filters: {
    search: typeof route.query.search === 'string' ? route.query.search : null,
    brandCode: brandOptions.some(({ value }) => value === route.query.brandCode) ? route.query.brandCode : null,
  },
  currentPage: Math.max(1, Number(route.query.page) || 1),
  itemsPerPage: 10,
  totalResults: 0,
  records: [],
  isLoadingRecords: false,
  isDeletingRecord: false,
  downloadingPdf: null,
  errorMessage: null,
  selectedRecord: null,
  deleteDialogOpen: false,
})

const listReturnQuery = computed(() => ({
  ...(state.currentPage > 1 ? { page: state.currentPage } : {}),
  ...(state.filters.search ? { search: state.filters.search } : {}),
  ...(state.filters.brandCode ? { brandCode: state.filters.brandCode } : {}),
}))

const filterFields = [
  {
    key: 'search',
    type: 'search',
    placeholder: 'Pretraži po tipu vozila, registraciji, imenu ili tvrtki',
    width: '600px',
  },
  {
    key: 'brandCode',
    type: 'select',
    placeholder: 'SVE MARKE',
    items: brandOptions,
    width: '230px',
  },
]

const recordTableHeaders = [
  { title: 'Vozilo', key: 'vehicleName', sortable: false, width: '18%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Korisnik', key: 'fullName', sortable: false, width: '16%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Razdoblje', key: 'usageFrom', sortable: false, width: '14%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Status', key: 'status', sortable: false, width: '9%' },
  { title: 'Zapisnik o preuzimanju', key: 'pickup', sortable: false, width: '20%' },
  { title: 'Zapisnik o povratku', key: 'return', sortable: false, width: '18%' },
  { title: '', key: 'actions', sortable: false, align: 'end', width: '5%' },
]

const recordStatusLabels = {
  draft: 'Draft',
  'picked-up': 'Preuzeto',
  returned: 'Vraćeno',
}

const recordStatusClasses = {
  draft: 'records-list__draft-status',
  'picked-up': 'status-chip--occupied',
  returned: 'status-chip--archived',
}

const selectedRecordSubtitle = computed(() => state.selectedRecord
  ? `${getEventBrandName(state.selectedRecord.vehicleBrandCode)} ${state.selectedRecord.vehicleName} · ${state.selectedRecord.vehiclePlate} · ${getRecordUserLabel(state.selectedRecord)}`
  : '')

onMounted(() => loadRecordsAsync(state.currentPage))

function getRecordUserLabel (record) {
  return [record.fullName, record.company].filter(Boolean).join(', ') || '—'
}

function getRecordPeriod (record) {
  if (!record.usageFrom && !record.usageTo) {
    return '—'
  }

  return `${record.usageFrom ? formatDate(record.usageFrom) : '—'} – ${record.usageTo ? formatDate(record.usageTo) : '—'}`
}

function isRecordStageEditable (record, stage) {
  return stage === 'pickup' ? record.status === 'draft' : record.status === 'picked-up'
}

function getRecordPageRoute (record, stage) {
  const isEditable = isRecordStageEditable(record, stage)
  const routeName = stage === 'pickup'
    ? (isEditable ? 'fleet-record-pickup-form' : 'fleet-record-pickup-view')
    : (isEditable ? 'fleet-record-return-form' : 'fleet-record-return-view')

  return router.hasRoute(routeName)
    ? { name: routeName, params: { recordId: record.id }, ...(isEditable ? {} : { query: listReturnQuery.value }) }
    : null
}

function openDeleteDialog (record) {
  state.selectedRecord = record
  state.deleteDialogOpen = true
}

async function loadRecordsAsync (page = 1) {
  const requestId = ++latestRecordsRequestId
  state.isLoadingRecords = true
  state.errorMessage = null

  try {
    const response = await apiClient.getAsync('/api/drivepark/records', {
      queryParams: {
        search: state.filters.search || null,
        brandCode: state.filters.brandCode,
        page,
        perPage: state.itemsPerPage,
      },
    })

    if (requestId !== latestRecordsRequestId) {
      return
    }

    const lastPage = getPageCount(response.pagination.totalResults, response.pagination.perPage)

    if (page > lastPage) {
      await loadRecordsAsync(lastPage)
      return
    }

    state.records = response.results
    state.currentPage = response.pagination.page
    state.itemsPerPage = response.pagination.perPage
    state.totalResults = response.pagination.totalResults
  } catch (error) {
    console.error('Failed to load drive park records.', error)

    if (requestId === latestRecordsRequestId) {
      state.errorMessage = error.status === 400 ? 'Odabrana marka nije valjana.' : 'Zapisnike nije bilo moguće učitati.'
      state.records = []
      state.totalResults = 0
    }
  } finally {
    if (requestId === latestRecordsRequestId) {
      state.isLoadingRecords = false
    }
  }
}

async function downloadRecordPdfAsync (record, stage) {
  state.downloadingPdf = `${record.id}-${stage}`

  try {
    const response = await apiClient.getAsync(
      `/api/drivepark/records/${record.id}/pdf/${stage}`,
      { responseType: 'response', headers: { Accept: 'application/pdf' } }
    )
    await downloadResponseFileAsync(response, `Zapisnik-o-${stage === 'pickup' ? 'preuzimanju' : 'vracanju'}-${record.id}.pdf`)
  } catch (error) {
    console.error('Failed to download a drive park record PDF.', error)
    if (error.status === 404) showErrorToast('PDF zapisnika nije dostupan.')
    else showRequestErrorToast(error, 'PDF zapisnika nije bilo moguće preuzeti.')
  } finally {
    state.downloadingPdf = null
  }
}

async function deleteRecordAsync () {
  const record = state.selectedRecord

  if (state.isDeletingRecord || record?.status !== 'draft') {
    return
  }

  state.isDeletingRecord = true

  try {
    await apiClient.deleteAsync(`/api/drivepark/records/${record.id}/pickup`)
    state.deleteDialogOpen = false

    showSuccessToast('Zapisnik je uspješno uklonjen.')
    await loadRecordsAsync(state.currentPage)
  } catch (error) {
    console.error('Failed to delete a drive park record.', error)
    showRequestErrorToast(error, 'Zapisnik nije bilo moguće ukloniti.')

    if (error.status === 404 || error.status === 409) {
      state.deleteDialogOpen = false
      await loadRecordsAsync(state.currentPage)
    }
  } finally {
    state.isDeletingRecord = false
  }
}
</script>

<style scoped lang="scss">
.records-list {
  &__table {
    :deep(.v-table__wrapper) {
      overflow-x: auto;
    }

    :deep(table) {
      min-width: 1280px;
    }
  }

  &__document-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 0;
  }

  &__document-button.v-btn {
    height: 32px;
    padding: 0 8px;
    font-size: 11px;
  }

  &__draft-status {
    background: color-mix(in srgb, var(--color-warning) 12%, white);
    color: var(--color-warning);
  }
}
</style>
